import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { Action, Alert, Badge, Icon, List } from 'corso-admin-ui';
import { CrewMerchantApi, CrewUi } from 'corso-types';
import { useCallback, useEffect, useMemo } from 'react';
import { useVerifyAddress } from '~/hooks/useReturnLocations';

type LocationAddress = CrewMerchantApi.RequestBody<
  '/:storeId/verify-address',
  'post'
>;

const fieldLabelMap: Record<string, string> = {
  street1: 'Address Line 1',
  street2: 'Address Line 2',
  city: 'City',
  state: 'State/Province',
  zip: 'Postal Code',
  country: 'Country',
};

const epFieldToLocationKey = {
  street1: 'line1',
  street2: 'line2',
  city: 'city',
  zip: 'postalCode',
  state: 'stateOrProvinceCode',
  country: 'countryCode',
} as const satisfies Record<string, keyof CrewUi.ReturnLocationAddress>;

function isEpField(field: string): field is keyof typeof epFieldToLocationKey {
  return field in epFieldToLocationKey;
}

function toLocationCorrections(
  corrections: { field: string; suggested?: string | null }[],
) {
  return corrections.reduce<Partial<CrewUi.ReturnLocationAddress>>(
    (acc, { field, suggested }) => {
      if (!suggested || !isEpField(field)) return acc;
      return { ...acc, [epFieldToLocationKey[field]]: suggested };
    },
    {},
  );
}

export default function VerifyAddressButton({
  address,
  onApplyCorrections,
}: {
  address: LocationAddress;
  onApplyCorrections: (
    corrected: Partial<CrewUi.ReturnLocationAddress>,
  ) => void;
}) {
  const {
    mutateAsync: verify,
    isPending,
    data: result,
    reset,
  } = useVerifyAddress();

  const addressKey = useMemo(
    () =>
      [
        address.line1,
        address.city,
        address.postalCode,
        address.countryCode,
        address.stateOrProvinceCode,
      ].join('|'),
    [
      address.line1,
      address.city,
      address.postalCode,
      address.countryCode,
      address.stateOrProvinceCode,
    ],
  );

  useEffect(() => reset(), [addressKey, reset]);

  const handleVerify = useCallback(() => {
    verify(address).catch(console.error);
  }, [verify, address]);

  const hasCorrections = result?.ok === true && 'corrections' in result;

  return (
    <div className="flex flex-col gap-2">
      {result?.ok === true && !hasCorrections && (
        <div>
          <Badge variant="success">
            <Icon icon={faCircleCheck} />
            Address verified
          </Badge>
        </div>
      )}

      {hasCorrections && (
        <Alert variant="warning" heading="Address Corrections Suggested">
          <List items={result.corrections} columns={1}>
            {(c) => (
              <List.Item key={c.field}>
                {fieldLabelMap[c.field] ?? c.field}:{' '}
                <span className="line-through">{c.original}</span>
                {' \u2192 '}
                <span className="font-medium">{c.suggested}</span>
              </List.Item>
            )}
          </List>
        </Alert>
      )}

      {result?.ok === false && (
        <Alert variant="critical" heading="Address Verification Failed">
          <List items={result.errors} columns={1}>
            {(err) => <List.Item key={err}>{err}</List.Item>}
          </List>
        </Alert>
      )}

      {hasCorrections ? (
        <Action
          variant="ghost"
          type="button"
          icon="warningInline"
          onClick={() =>
            onApplyCorrections(toLocationCorrections(result.corrections))
          }
        >
          Apply corrections
        </Action>
      ) : (
        <Action
          variant="ghost"
          type="button"
          icon={faCircleCheck}
          onClick={handleVerify}
          loading={isPending}
        >
          Verify Address
        </Action>
      )}
    </div>
  );
}
